import * as React from 'react';
import { useCallback } from 'react';
import cn from 'classnames';
import MediaQuery from 'react-responsive';
import { format, MOBILE_MIN_WIDTH } from '../../../../../../../utils';
import { DatepickerTab } from '../Datepicker';
import { useTheme } from '../../../../../../../theme';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../../../../../../context';
import { tryParseDate } from './utils';
import ManualInput from './ManualInput/ManualInput';
import { CdnIcon } from '@shared/ui';

export interface Props {
	className?: string;
	isOpen: boolean;
	open: () => void;
	close: () => void;
	setRouteType: (route: DatepickerTab) => void;
	segmentId: number;
	withDateBack?: boolean;
	routeType: DatepickerTab;
	dateTo: Date;
	dateBack?: Date;
	onClear?: () => void;
	onClickDatepickerTabReturn?: () => void;
	onClickDatepickerTabOutbound?: () => void;
	onDateSelect?: (date: Date, isBackDate?: boolean) => void;
	setIsRouteTypeSetManually: React.Dispatch<boolean>;
	isReturnDateDisabled?: boolean;
}

const Value: React.FC<Props> = ({
	className,
	isOpen,
	open,
	close,
	setRouteType,
	segmentId,
	withDateBack,
	routeType,
	dateBack,
	dateTo,
	onClear,
	onDateSelect,
	setIsRouteTypeSetManually,
	onClickDatepickerTabReturn,
	onClickDatepickerTabOutbound,
	isReturnDateDisabled = false
}) => {
	const { Datepicker: css } = useTheme('SearchForm');
	const { t } = useTranslation('SearchForm');

	const { mobileFormType, proMode, separatedDatepicker } = useConfig().SearchForm;
	const alternative = mobileFormType !== 'common';

	const onManualDateEnter = (value: string) => {
		const parseDate = tryParseDate(value);

		if (parseDate) {
			onDateSelect(parseDate, routeType === DatepickerTab.Return);
		}
	};

	const handleRouteTypeChange = useCallback(
		(e: React.MouseEvent, routeType: DatepickerTab) => {
			// WEBSKY-7796: если не выбрана дата "туда", не даем выбрать дату "обратно"
			if (routeType === DatepickerTab.Return && isReturnDateDisabled) {
				e.preventDefault();
				e.stopPropagation();

				return;
			}

			if (isOpen) {
				e.preventDefault();
				e.stopPropagation();
				setRouteType(routeType);
				setIsRouteTypeSetManually(true);
			}

			if (routeType === DatepickerTab.Return && onClickDatepickerTabReturn) {
				onClickDatepickerTabReturn();
			}

			if (routeType === DatepickerTab.Outbound && onClickDatepickerTabOutbound) {
				onClickDatepickerTabOutbound();
			}
		},
		[routeType, isOpen, dateTo, dateBack, isReturnDateDisabled]
	);

	const handleClear = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		e.preventDefault();
		e.stopPropagation();
		onClear();

		if (separatedDatepicker) {
			close();
		}
	};

	return (
		<div
			data-test-id="search-form-dates"
			data-segment-id={segmentId}
			className={cn(className, css.dates, `${css.dates}_${segmentId}`, {
				[css.oneDate]: !withDateBack,
				[css.dates_focused]: isOpen,
				[css.alternative]: alternative
			})}
			onClick={() => {
				isOpen ? close() : open();
			}}
		>
			<MediaQuery minWidth={+MOBILE_MIN_WIDTH + 1}>
				<div
					data-test-id="search-form-departure-date"
					className={cn(css.dates__to, {
						[css.dates__to_focused]: isOpen && routeType === DatepickerTab.Outbound
					})}
				>
					<ManualInput
						onEnter={onManualDateEnter}
						showInput={isOpen && proMode && routeType === DatepickerTab.Outbound}
						inputClassName={css.date__input}
					>
						<CdnIcon className={css.dates__icon} icon={'Action/Show Calendar'} />
						{dateTo && (
							<div
								className={css.date__inputWrapper}
								onClick={e => {
									handleRouteTypeChange(e, DatepickerTab.Outbound);
								}}
							>
								{format(dateTo, 'dd MMM')}
								<span className={css.dow}>
									{dateBack || isOpen || separatedDatepicker
										? format(dateTo, 'eeeeee')
										: format(dateTo, 'eeee')}
								</span>
							</div>
						)}
						{!dateTo && (isOpen || separatedDatepicker) && (
							<span onClick={e => handleRouteTypeChange(e, DatepickerTab.Outbound)}>{t('Outbound')}</span>
						)}

						{!separatedDatepicker && !dateTo && !dateBack && !isOpen && (
							<span className={css.placeholder}>{t('Dates')}</span>
						)}
					</ManualInput>
				</div>

				{withDateBack && (isOpen || dateBack || separatedDatepicker) && (
					<div
						data-test-id="search-form-return-date"
						className={cn(css.dates__back, {
							[css.dates__back_focused]: isOpen && routeType === DatepickerTab.Return,
							[css.dates__back_disabled]: isReturnDateDisabled
						})}
					>
						<ManualInput
							onEnter={onManualDateEnter}
							showInput={isOpen && proMode && routeType === DatepickerTab.Return}
							inputClassName={cn(css.date__input, css.date__input_back)}
						>
							{dateBack ? (
								<div
									onClick={e => {
										handleRouteTypeChange(e, DatepickerTab.Return);
									}}
								>
									{format(dateBack, 'dd MMM')}
									<span className={css.dow}>{format(dateBack, 'eeeeee')}</span>
								</div>
							) : (
								<span onClick={e => handleRouteTypeChange(e, DatepickerTab.Return)}>{t('Return')}</span>
							)}
						</ManualInput>
					</div>
				)}

				{(dateTo || dateBack) && isOpen && (
					<div className={css.returnDelete} data-test-id="search-form-clear-dates" onClick={handleClear}>
						<CdnIcon icon={'Action/Close-Clear'} />
					</div>
				)}
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				<div
					data-test-id="search-form-departure-date"
					className={cn(css.dates__to)}
					onClick={e => handleRouteTypeChange(e, DatepickerTab.Outbound)}
				>
					<span className={css.dates__to_text}>
						{dateTo && (
							<span className={css.dates__to_text}>
								{format(dateTo, 'dd MMM')}
								<span className={css.dow}>{format(dateTo, 'eeeeee')}</span>
							</span>
						)}

						{!separatedDatepicker && dateBack && (
							<span className={css.dates__to_text}>
								{` — ${format(dateBack, 'dd MMM')}`}
								<span className={css.dow}>{format(dateBack, 'eeeeee')}</span>
							</span>
						)}
					</span>

					{!separatedDatepicker && alternative && dateTo && (
						<div className={css.alternative_dow}>
							<span>{format(dateTo, 'eeee')}</span>

							{dateBack && <span>{` — ${format(dateBack, 'eeee')}`}</span>}
						</div>
					)}

					{!dateTo &&
						(separatedDatepicker || isOpen ? (
							t('Outbound')
						) : (
							<>
								<CdnIcon className={css.dates__icon} icon={'Action/Show Calendar'} />
								<span className={css.placeholder}>{t('Dates')}</span>
							</>
						))}
				</div>

				{withDateBack && separatedDatepicker && (
					<div
						data-test-id="search-form-return-date"
						className={cn(css.dates__back)}
						onClick={e => handleRouteTypeChange(e, DatepickerTab.Return)}
					>
						<span className={css.dates__to_text}>
							{dateBack && (
								<span className={css.dates__to_text}>
									{format(dateBack, 'dd MMM')}
									<span className={css.dow}>{format(dateBack, 'eeeeee')}</span>
								</span>
							)}
						</span>

						{alternative && dateBack && (
							<div className={css.alternative_dow}>
								<span>{format(dateBack, 'eeee')}</span>
							</div>
						)}

						{!dateBack && t('Return')}
					</div>
				)}
			</MediaQuery>
		</div>
	);
};

export default Value;
